import ProductionHierarchyAPIService from 'frontcore/data/production-hierarchy/ProductionHierarchyAPIService'
import { computed, ComputedRef, reactive } from 'vue'
import { defineStore } from 'pinia'
import { ProductionHierarchy, ProductionHierarchyLevel } from 'frontcore/domain/production-hierarchy/types'
import { PROMISE_STATUS } from 'frontcore/constants'
import { Proxy } from 'frontcore/constants/proxy'


interface IProductionHierarchyState {
  productionHierarchy: Proxy<ProductionHierarchy[]>
  productionHierarchyLevels: ProductionHierarchyLevel[]
}

interface IGetProductionHierarchyByLevel {
  level: number
  includeDisabled: boolean
  disabledExceptions?: number[]
  selectedParents?: number[]
}

interface IProductionHierarchyStore {
  fetchProductionHierarchy: (force: boolean) => Promise<void>
  productionHierarchy: ComputedRef<ProductionHierarchy[]>
  areProductionHierarchyLoaded: ComputedRef<boolean>
  areProductionHierarchyCalled: ComputedRef<boolean>
  getProductionHierarchyLevels: ComputedRef<ProductionHierarchyLevel[]>
  getProductionHierarchyByLevel: (params: IGetProductionHierarchyByLevel) => ProductionHierarchy[]
  setProductionHierarchyLevels: (levels: ProductionHierarchyLevel[]) => void
}

export const useProductionHierarchyStore = defineStore('ProductionHierarchy', (): IProductionHierarchyStore => {
  const { fetch } = new ProductionHierarchyAPIService()

  const state = reactive<IProductionHierarchyState>({
    productionHierarchy: {
      data: [],
      promise: Promise.resolve([]),
      promiseStatus: PROMISE_STATUS.notStarted,
    },
    productionHierarchyLevels: [],
  })

  const fetchProductionHierarchy = async (force = false) => {
    if (state.productionHierarchy.promiseStatus === PROMISE_STATUS.notStarted || force) {
      state.productionHierarchy.promise = fetch()
      state.productionHierarchy.promiseStatus = PROMISE_STATUS.pending
    }

    try {
      if (state.productionHierarchy.data.length === 0 || force) {
        const hierarchy = await state.productionHierarchy.promise
        state.productionHierarchy.data = hierarchy
        state.productionHierarchy.promiseStatus = PROMISE_STATUS.complete
        
      }
    } catch (e) {
      state.productionHierarchy.promiseStatus = PROMISE_STATUS.notStarted
    }
  }

  const getProductionHierarchyLevels = computed(() => state.productionHierarchyLevels)

  const setProductionHierarchyLevels = (levels: ProductionHierarchyLevel[]) => {
    state.productionHierarchyLevels = levels
  }

  const filterDisabled = (productionHierarchy: ProductionHierarchy[], exceptions: number[]) => {
    return productionHierarchy.filter(item => item.enabled || exceptions.includes(item.id))
  }

  const filterByParent = (productionHierarchy: ProductionHierarchy[], parents: number[], exceptions: number[]) => {
    return productionHierarchy.filter(
      item => item.parent === null
      || parents.includes(item.parent)
      || exceptions.includes(item.id)
    )
  }

  const filterByLevel = (productionHierarchy: ProductionHierarchy[], level: number) => {
    return productionHierarchy.filter(item => item.level === level)
  }

  const getProductionHierarchyByLevel = ({
    level,
    includeDisabled,
    disabledExceptions=[],
    selectedParents=[],
  }: IGetProductionHierarchyByLevel) => {
    let filteredByLevel = filterByLevel(state.productionHierarchy.data, level)
    if (selectedParents.length > 0) {
      filteredByLevel = filterByParent(filteredByLevel, selectedParents, disabledExceptions)
    }
    return includeDisabled ? filteredByLevel : filterDisabled(filteredByLevel, disabledExceptions)
  }

  const productionHierarchy = computed(() => state.productionHierarchy.data)

  const areProductionHierarchyCalled = computed(() => state.productionHierarchy.promiseStatus !== PROMISE_STATUS.notStarted)
  const areProductionHierarchyLoaded = computed(() => state.productionHierarchy.promiseStatus === PROMISE_STATUS.complete)

  return {
    fetchProductionHierarchy,
    productionHierarchy,
    areProductionHierarchyLoaded,
    areProductionHierarchyCalled,
    getProductionHierarchyLevels,
    getProductionHierarchyByLevel,
    setProductionHierarchyLevels,
  }
})
